import React from 'react';
import { Box, Theme, Typography } from '@mui/material';
import { CampaignCodeReward, RewardType } from '@loyalty/shared-common/rewards-api-client';
import { Controller, useFormContext } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { QlfFormRow, QlfNumberField, useQlfTranslation } from '@qualifio/ui-commons';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { Title } from '../../../shared/layout/Title';
import { useFeatures } from '../../../features/FeaturesContext';
import { useSelectiveEffect } from '../../../shared/use-selective-effect';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  subTitle: {
    marginBottom: spacing(2),
  },
}));

export function CampaignCodeDetails({
  reward,
}: {
  reward: CampaignCodeReward;
}): React.ReactElement {
  const classes = useStyles();
  const { features } = useFeatures();
  const { write: rewardWriteEnabled } = features[Feature.REWARD];
  const { t } = useTranslation();
  const { t: htmlT } = useQlfTranslation('translation');
  const { control, watch } = useFormContext<CampaignCodeReward>();

  if (reward.type !== RewardType.CAMPAIGN_CODE) {
    throw new Error('This component is only for campaign code rewards');
  }

  useSelectiveEffect(
    () => {
      const codeListId = reward?.data?.campaignCodeData?.codeListId;
    },
    [reward.id, reward?.data?.campaignCodeData?.codeListId, reward.updatedAt],
    [reward.id, reward.updatedAt],
  );

  const formValue = watch();

  return (
    <Box>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <Title>{t('rewards.campaign-code.title')}</Title>
      </Box>
      <Box className={classes.subTitle}>
        <Typography variant="body1">
          {htmlT('rewards.campaign-code.message', {
            url: 'https://support.qualifio.com/hc/en-us/articles/217958888-Unique-coupon-codes-for-Qualifio',
          })}
        </Typography>
      </Box>
      <Box>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QlfFormRow label={t('reward.campaign-code.code-list-id')} mandatory>
              <Controller
                control={control}
                name="data.campaignCodeData.codeListId"
                defaultValue={formValue?.data?.campaignCodeData?.codeListId}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <QlfNumberField
                    {...field}
                    inputRef={ref}
                    placeholder="XXXXX"
                    error={!!error}
                    helperText={error?.message}
                    sx={{
                      marginLeft: 10,
                      marginBottom: 0,
                      width: 125,
                    }}
                    InputProps={{
                      startAdornment: <span id="code-prefix">BURN:</span>,
                    }}
                    disabled={!rewardWriteEnabled || !!reward?.deletedAt}
                  />
                )}
              />
            </QlfFormRow>
          </Box>
        </div>
      </Box>
    </Box>
  );
}
